import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage, useField, useFormikContext } from "formik";
import styled from "styled-components";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import {
  registrationInitialValues,
  validationSchema,
} from "./registrationSchema";
import { ErrorText } from "../../../../shared/inputs/ErrorText";
import { signup } from "../../../../../services/users/user-services";
import { getApiErrorMessage, getApiErrorTitle, getSignupSuccessCopy } from "../../../../../utils/apiErrorMessage";
import { getSubscriptions } from "../../../../../services/subscriptions/subscriptions-services";
import { getPlatformStatus } from "../../../../../services/admin/admin-platform-service";
import { useNavigate } from "react-router-dom";
import { FormAlert } from "../../../../shared/alerts/FormAlert";
import { ButtonSpinner } from "../../../../shared/loading/LoadingScreen";
import Modal from "../../../../shared/modal/Modal";
import {
  FALLBACK_DIAL_OPTIONS,
  toFlagEmoji,
} from "../../../../../utils/dialCodes";
import { findLocation } from "../../../../../utils/locations";
import { fetchLocations } from "../../../../../services/locations/locations-service";
// SMS verification disabled temporarily
// import { sendMobileOtp, verifyMobileOtp } from "../../../../../services/phase/phase-service";
import { SubscriptionComparisonTable } from "../../../../shared/sections/subscription-plan/SubscriptionComparisonTable";
import CaptchaField from "../../../../shared/inputs/CaptchaField";
import { fontFamily } from "../../../../shared/styles/theme";
import { primaryColor, white } from "../../../../shared/styles/color";
import { googleLogo } from "../../../../../assets/s3Assets";
import {
  FiBriefcase,
  FiLock,
  FiMail,
  FiPhone,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { FaLinkedinIn } from "react-icons/fa";

const RegistrationForm = () => {
  const navigate = useNavigate();

  const [dialOptions] = useState(FALLBACK_DIAL_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [formAlert, setFormAlert] = useState(null);
  const [hasAgreedToPrivacyPolicy, setHasAgreedToPrivacyPolicy] =
    useState(false);
  const [hasAgreedToMarketing, setHasAgreedToMarketing] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaChallenge, setCaptchaChallenge] = useState({});
  // SMS verification disabled temporarily
  // const [otpSent, setOtpSent] = useState(false);
  // const [otpSending, setOtpSending] = useState(false);
  const [registrationClosed, setRegistrationClosed] = useState(false);
  const [closedMessage, setClosedMessage] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  useEffect(() => {
    fetchLocations().then(setLocationOptions).catch(() => setLocationOptions([]));
  }, []);
  useEffect(() => {
    getPlatformStatus()
      .then((res) => {
        const data = res?.data?.result?.data;
        if (data?.userRegistration === false) {
          setRegistrationClosed(true);
          setClosedMessage(
            data?.registrationMessage ||
              "New user registration is currently disabled. Please check back later.",
          );
        }
      })
      .catch(() => {});
  }, []);

  if (registrationClosed) {
    return (
      <FormCard>
        <FormTitle>Registration Unavailable</FormTitle>
        <ClosedNotice>{closedMessage}</ClosedNotice>
      </FormCard>
    );
  }

  return (
    <FormCard>
      <FormHeader>
        <FormTitle>Sign up to Liber</FormTitle>
        <LoginPrompt>
          Already have an account?{" "}
          <TextLink type="button" onClick={() => navigate("/login")}>
            Log in
          </TextLink>
        </LoginPrompt>
      </FormHeader>

      {formAlert && (
        <FormAlert
          variant={formAlert.variant}
          title={formAlert.title}
          message={formAlert.message}
          onDismiss={() => setFormAlert(null)}
        />
      )}

      <Formik
        initialValues={registrationInitialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          setFormAlert(null);

          if (!hasAgreedToPrivacyPolicy) {
            setFormAlert({
              variant: "warning",
              title: "Privacy Policy",
              message: "You must agree to the Privacy Policy!",
            });
            return;
          }
          if (!captchaValid) {
            setFormAlert({
              variant: "warning",
              title: "Verification",
              message: "Please complete the CAPTCHA.",
            });
            return;
          }
          const loc = findLocation(values.location);
          const payload = {
            ...values,
            mobile: `${values.mobile_country_code || ""}${values.mobile_number || ""}`,
            location_city: loc?.city || values.location,
            location_country: loc?.country || "",
            location_lat: loc?.lat ?? null,
            location_lng: loc?.lng ?? null,
            mobile_verified: true,
            hasAgreedToPrivacyPolicy,
            hasAgreedToMarketing,
            ...captchaChallenge,
          };
          delete payload.mobile_country_code;
          delete payload.mobile_number;
          delete payload.otp_code;
          delete payload.location;

          setLoading(true);
          // SMS verification disabled temporarily
          // await verifyMobileOtp(payload.mobile, values.otp_code);
          try {
            const result = await signup(payload);
            if (!result) {
              setFormAlert({
                variant: "danger",
                title: "Network error",
                message:
                  "Could not reach the server. Check your connection and try again.",
              });
              return;
            }

            const status = result.status;
            const apiMessage = getApiErrorMessage(result);

            if (status === 201) {
              const user =
                result?.data?.user ||
                result?.data?.result?.data ||
                result?.data?.result;
              const { title, text } = getSignupSuccessCopy({
                response: result,
                user,
                email: values.email,
              });
              const isFreelancer =
                user?.user_type === "freelancers" ||
                user?.user_type === "freelancer";
              navigate("/login", {
                replace: true,
                state: {
                  registrationSuccess: {
                    title,
                    message: text,
                  },
                  ...(isFreelancer ? { onboardingSubscribe: true } : {}),
                },
              });
              return;
            }

            if (status === 403) {
              setFormAlert({
                variant: "info",
                title: getApiErrorTitle(status, apiMessage),
                message:
                  apiMessage ||
                  "New user registration is currently disabled.",
              });
              return;
            }

            setFormAlert({
              variant: status === 409 ? "warning" : "danger",
              title: getApiErrorTitle(status, apiMessage),
              message: apiMessage,
            });
          } finally {
            setLoading(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          return (
            <StyledForm onSubmit={handleSubmit}>
              <RoleTabRow>
                <RoleTab
                  type="button"
                  $active={values.user_type === "freelancers"}
                  onClick={() => setFieldValue("user_type", "freelancers")}
                >
                  <FiUser size={18} />
                  I&apos;m a Freelancer
                </RoleTab>
                <RoleTab
                  type="button"
                  $active={values.user_type === "business"}
                  onClick={() => setFieldValue("user_type", "business")}
                >
                  <FiBriefcase size={18} />
                  I&apos;m a Business
                </RoleTab>
              </RoleTabRow>
              <ErrorMessage name="user_type" component={ErrorText} />

              <NameRow>
                <SignupField
                  name="first_name"
                  placeholder="First Name"
                  icon={FiUser}
                />
                <SignupField
                  name="last_name"
                  placeholder="Last Name"
                  icon={FiUser}
                />
              </NameRow>

              {values.user_type === "business" && (
                <SignupField
                  name="company_name"
                  placeholder="Company name"
                  icon={FiBriefcase}
                />
              )}

              <SignupField
                name="email"
                type="email"
                placeholder="Email Address"
                icon={FiMail}
              />

              <PhoneRow>
                <PhoneCode>
                  <DialCodeField
                    name="mobile_country_code"
                    options={dialOptions}
                  />
                </PhoneCode>
                <PhoneNumber>
                  <SignupField
                    name="mobile_number"
                    type="tel"
                    placeholder="Phone Number"
                    icon={FiPhone}
                    onChange={(e) => {
                      const digitsOnly = String(e.target.value || "").replace(
                        /[^\d]/g,
                        "",
                      );
                      setFieldValue("mobile_number", digitsOnly);
                    }}
                  />
                </PhoneNumber>
              </PhoneRow>
              <ErrorMessage name="mobile_country_code" component={ErrorText} />

              <SignupField
                name="password"
                type="password"
                placeholder="Create Password"
                icon={FiLock}
              />
              <SignupField
                name="password2"
                type="password"
                placeholder="Retype Password"
                icon={FiLock}
              />

              <LocationSelectField
                name="location"
                options={locationOptions.length ? locationOptions : [{ value: "", label: "Loading locations…" }]}
                labelText="Location"
                placeholder="Select your location"
              />

              {/* SMS verification disabled temporarily
              <OtpSection otpSent={otpSent} otpSending={otpSending} setOtpSent={setOtpSent} setOtpSending={setOtpSending} showAlert={showAlert} />
              */}

              {/* Freelancer plan comparison hidden on registration — choose plan after signup
              <FreelancerPlanExplainer />
              */}

              <CaptchaField
                onValidatedChange={setCaptchaValid}
                onChallengeChange={setCaptchaChallenge}
              />

              <CheckboxContainer>
                <ConsentCheckbox
                  type="checkbox"
                  id="privacy_consent"
                  onChange={(event) => {
                    setHasAgreedToPrivacyPolicy(event.target.checked);
                  }}
                />
                <span>
                  I have read and agree to the{" "}
                  <LinkButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPrivacyModalOpen(true);
                    }}
                  >
                    Privacy Policy
                  </LinkButton>{" "}
                  &amp;{" "}
                  <LinkButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsTermsModalOpen(true);
                    }}
                  >
                    Terms &amp; Conditions.
                  </LinkButton>
                </span>
              </CheckboxContainer>
              <Modal
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
                title="Terms & Conditions"
                modalContainerWidth="50%"
              >
                <TermsModalContent />
              </Modal>
              <Modal
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
                title="Privacy Policy"
                modalContainerWidth="50%"
              >
                <PrivacyModalContent />
              </Modal>
              <ErrorMessage
                name="agree_to_privacy_policy"
                component={ErrorText}
              />
              <CheckboxContainer>
                <ConsentCheckbox
                  type="checkbox"
                  id="marketing_consent"
                  onChange={(event) => {
                    setHasAgreedToMarketing(event.target.checked);
                  }}
                />
                <span>I consent to receive newsletters &amp; marketing updates</span>
              </CheckboxContainer>

              {loading ? (
                <ButtonSpinner />
              ) : (
                <CreateAccountBtn
                  type="submit"
                  disabled={!hasAgreedToPrivacyPolicy}
                >
                  Create Account
                </CreateAccountBtn>
              )}

              {/* Social signup hidden for now
              <DividerRow>
                <DividerLine />
                <DividerText>or sign up with</DividerText>
                <DividerLine />
              </DividerRow>

              <SocialRow>
                <SocialBtn type="button" disabled title="Coming soon">
                  <SocialIcon src={googleLogo} alt="" />
                  Google
                </SocialBtn>
                <SocialBtn type="button" disabled title="Coming soon">
                  <LinkedInIcon aria-hidden>
                    <FaLinkedinIn size={18} />
                  </LinkedInIcon>
                  LinkedIn
                </SocialBtn>
              </SocialRow>
              */}

              <SecurityNote>
                <FiShield size={16} />
                <span>
                  Your information is safe with us. We{" "}
                  <strong>never share</strong> your data.
                </span>
              </SecurityNote>
            </StyledForm>
          );
        }}
      </Formik>
    </FormCard>
  );
};

function SignupField({
  name,
  type = "text",
  placeholder,
  icon: Icon,
  onChange,
}) {
  const [field, meta] = useField(name);
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && showPassword ? "text" : type;

  return (
    <FieldWrap>
      <InputShell>
        {Icon && (
          <LeadingIcon aria-hidden>
            <Icon size={18} />
          </LeadingIcon>
        )}
        <StyledSignupInput
          {...field}
          type={inputType}
          placeholder={placeholder}
          onChange={onChange || field.onChange}
        />
        {isPassword && (
          <TogglePassword
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </TogglePassword>
        )}
      </InputShell>
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FieldWrap>
  );
}

function OtpSectionDisabled({ otpSent, otpSending, setOtpSent, setOtpSending, showAlert }) {
  return null;
  /* SMS verification disabled temporarily
  const { values, setFieldValue } = useFormikContext();
  return (
    <>
      <OtpRow>
        <SignupField name="otp_code" type="text" placeholder="SMS verification code" icon={FiShield} maxLength={6} />
        <OtpBtn
          type="button"
          disabled={otpSending}
          onClick={async () => {
            const mobile = `${values.mobile_country_code || ""}${values.mobile_number || ""}`;
            if (!mobile || mobile.length < 10) {
              showAlert({ title: "Phone", message: "Enter a valid phone number first.", variant: "warning" });
              return;
            }
            setOtpSending(true);
            try {
              const res = await sendMobileOtp(mobile);
              setOtpSent(true);
              showAlert({
                title: "Code sent",
                message: res?.data?.result?.dev_code
                  ? "Development mode: check server logs for your code."
                  : "Check your phone for the verification code.",
                variant: "success",
              });
            } catch {
              showAlert({ title: "Error", message: "Could not send verification code.", variant: "error" });
            } finally {
              setOtpSending(false);
            }
          }}
        >
          {otpSent ? "Resend code" : "Send code"}
        </OtpBtn>
      </OtpRow>
      <ErrorMessage name="otp_code" component={ErrorText} />
    </>
  );
  */
}

function FreelancerPlanExplainer() {
  const { values } = useFormikContext();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getSubscriptions(null, "monthly")
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.result?.data || res?.data?.data || [];
        if (Array.isArray(rows) && rows.length) {
          setPlans(rows.filter((r) => r.is_active !== false));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (values.user_type !== "freelancers") return null;
  return (
    <PlanExplainerWrap>
      <PlanNote>
        <strong>Freelancer plans</strong> — choose after signup. Start is free; Grow unlocks applications;
        Advance adds top placement and verified eligibility.
      </PlanNote>
      <ComparisonScroll>
        <SubscriptionComparisonTable monthly plans={plans} />
      </ComparisonScroll>
    </PlanExplainerWrap>
  );
}

function LocationSelectField({ name, options = [], labelText = "Location", placeholder = "Select your location" }) {
  const [field, meta] = useField(name);

  return (
    <FieldWrap>
      <SelectShell>
        <FloatingLabel>{labelText}</FloatingLabel>
        <NationalitySelect {...field} value={field.value || ""}>
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </NationalitySelect>
        <SelectChevron size={18} aria-hidden />
      </SelectShell>
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FieldWrap>
  );
}

export default RegistrationForm;

function dialCodeLabel(opt) {
  return `${toFlagEmoji(opt?.cca2)} ${opt?.value || ""}`.trim();
}

const DialCodeField = ({ name, options = [] }) => {
  const [field, meta, helpers] = useField(name);
  const selectId = `${name}-select`;

  return (
    <DialWrap>
      <DialSelectShell>
        <DialSelect
          id={selectId}
          name={name}
          value={field.value}
          onChange={(e) => helpers.setValue(e.target.value)}
          onBlur={field.onBlur}
          aria-label="Country calling code"
          aria-invalid={meta?.touched && meta.error ? "true" : "false"}
        >
          {options.map((opt) => (
            <option
              key={`${opt.cca2 || "x"}-${opt.value}`}
              value={opt.value}
              title={opt.label}
            >
              {dialCodeLabel(opt)}
            </option>
          ))}
        </DialSelect>
        <DialChevron size={18} aria-hidden />
      </DialSelectShell>
    </DialWrap>
  );
};

const PhoneRow = styled.div`
  display: grid;
  grid-template-columns: minmax(108px, 128px) 1fr;
  gap: 12px;
  width: 100%;
  align-items: start;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const PhoneCode = styled.div`
  width: 100%;
`;

const PhoneNumber = styled.div`
  width: 100%;
`;

const DialWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DialSelectShell = styled.div`
  position: relative;
  width: 100%;
`;

const DialSelect = styled.select`
  width: 100%;
  padding: 13px 28px 13px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: ${fontFamily.font};
  font-weight: 500;
  background: ${white};
  appearance: none;
  cursor: pointer;
  outline: none;
  color: #111827;
  box-sizing: border-box;
  min-height: 48px;

  &:focus {
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }
`;

const DialChevron = styled(ChevronDown)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  pointer-events: none;
`;

const ConsentCheckbox = styled.input`
  height: 18px;
  width: 18px;
  min-width: 18px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  background: white;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 2px;
  accent-color: ${primaryColor};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

export const Container = styled.div`
  min-height: 35rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  text-align: start;
`;

const OtpRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  & > div:first-child { flex: 1; }
  @media (max-width: 520px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const OtpBtn = styled.button`
  margin-top: 28px;
  padding: 10px 14px;
  border: 1px solid ${primaryColor};
  border-radius: 8px;
  background: ${white};
  color: ${primaryColor};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  @media (max-width: 520px) {
    margin-top: 0;
    width: 100%;
  }
`;

const PlanExplainerWrap = styled.div`
  margin: 8px 0 4px;
`;

const ComparisonScroll = styled.div`
  overflow-x: auto;
  margin-top: 8px;
  -webkit-overflow-scrolling: touch;
`;

const PlanNote = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 8px 0 12px;
`;

const FormCard = styled.div`
  background: ${white};
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px 28px 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  width: 100%;
  box-sizing: border-box;
  text-align: left;

  @media (max-width: 767px) {
    padding: 22px 18px 20px;
    border-radius: 12px;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 22px;
`;

const FormTitle = styled.h1`
  font-family: ${fontFamily.font};
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  color: #111827;
  margin: 0 0 8px;
  text-align: left;
`;

const LoginPrompt = styled.p`
  font-family: ${fontFamily.font};
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  text-align: left;
`;

const TextLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${primaryColor};
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const Title = FormTitle;

const ClosedNotice = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  max-width: 520px;
`;

export const StyledForm = styled(Form)`
  width: 100%;
  display: flex;
  gap: 14px;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
`;

const RoleTabRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

const RoleTab = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 10px;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p.$active ? primaryColor : "#e5e7eb")};
  background: ${white};
  color: ${(p) => (p.$active ? primaryColor : "#6b7280")};
  font-family: ${fontFamily.font};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;

  svg {
    flex-shrink: 0;
  }
`;

const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InputShell = styled.div`
  position: relative;
  width: 100%;
`;

const LeadingIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
`;

const StyledSignupInput = styled.input`
  width: 100%;
  padding: 13px 14px 13px 42px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: ${fontFamily.font};
  font-weight: 400;
  line-height: 1.4;
  outline: none;
  box-sizing: border-box;
  background: ${white};
  color: #111827;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }
`;

const TogglePassword = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;

  &:hover {
    color: #6b7280;
  }
`;

const SelectShell = styled.div`
  position: relative;
  width: 100%;
`;

const FloatingLabel = styled.span`
  position: absolute;
  top: -8px;
  left: 12px;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  background: ${white};
  padding: 0 4px;
  font-family: ${fontFamily.font};
  z-index: 1;
`;

const NationalitySelect = styled.select`
  width: 100%;
  padding: 14px 36px 14px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: ${fontFamily.font};
  font-weight: 400;
  background: ${white};
  appearance: none;
  cursor: pointer;
  outline: none;
  color: #111827;
  box-sizing: border-box;
  min-height: 48px;

  &:focus {
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }
`;

const SelectChevron = styled(ChevronDown)`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const CreateAccountBtn = styled.button`
  width: 100%;
  padding: 14px 20px;
  border: 0;
  border-radius: 10px;
  background: ${primaryColor};
  color: ${white};
  font-family: ${fontFamily.font};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`;

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 4px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e5e7eb;
`;

const DividerText = styled.span`
  font-family: ${fontFamily.font};
  font-size: 13px;
  color: #9ca3af;
  white-space: nowrap;
`;

const SocialRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: ${white};
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;

  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
  }
`;

const SocialIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const LinkedInIcon = styled.span`
  color: #0a66c2;
  display: flex;
  align-items: center;
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${fontFamily.font};
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  margin-top: 4px;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${primaryColor};
  }

  strong {
    color: ${primaryColor};
    font-weight: 600;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
  text-align: left;

  span {
    font-family: ${fontFamily.font};
    font-size: 13px;
    line-height: 1.5;
    color: #374151;
    flex: 1;
    min-width: 0;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #118ab2;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  &:hover {
    color: #0d6b8a;
  }
`;

const ModalContentContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  padding: 24px;
  font-family: "Poppins", sans-serif;
  background: #ffffff;
  text-align: left !important;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border-radius: 3px;
    
    &:hover {
      background: #a0a0a0;
    }
  }
  
  /* Force all headings to be left-aligned */
  h1, h2, h3, h4, h5, h6 {
    text-align: left !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    display: block !important;
  }
`;

const ModalHeading1 = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  margin-top: 0;
  margin-left: 0;
  margin-right: 0;
  color: #1d1e2b;
  border-bottom: 2px solid #118ab2;
  padding-bottom: 12px;
  text-align: left !important;
  display: block;
  width: 100%;
`;

const ModalHeading2 = styled.h2`
  font-size: 15px;
  font-weight: 600;
  margin-top: 24px;
  margin-bottom: 10px;
  margin-left: 0;
  margin-right: 0;
  color: #2c3e50;
  line-height: 1.4;
  text-align: left !important;
  display: block;
  width: 100%;
  
  &:first-of-type {
    margin-top: 0;
  }
`;

const ModalText = styled.p`
  font-size: 13px;
  line-height: 1.8;
  margin-bottom: 12px;
  color: #4a5568;
  text-align: justify;
  
  strong {
    color: #1d1e2b;
    font-weight: 600;
  }
`;

const TermsModalContent = () => (
  <ModalContentContainer>
    <ModalHeading1>Terms & Conditions</ModalHeading1>
    <ModalText>Effective Date: 29-09-2025</ModalText>
    <ModalText>Last Updated: 29-09-2025</ModalText>
    <ModalText>
      These Terms & Conditions ("Terms") govern your access to and use of the
      Liber platform (the "Platform"), operated by Liber FZE, a company
      registered in Sharjah Free Zone, United Arab Emirates. By registering or
      using Liber, you agree to be bound by these Terms.
    </ModalText>

    <ModalHeading2>1. Eligibility</ModalHeading2>
    <ModalText>- You must be 18 years or older to use Liber.</ModalText>
    <ModalText>
      - Freelancers worldwide may register, subject to compliance with
      applicable laws.
    </ModalText>
    <ModalText>
      - Businesses from both the UAE and internationally may register and engage
      freelancers.
    </ModalText>

    <ModalHeading2>2. Services Provided</ModalHeading2>
    <ModalText>
      Liber provides an online marketplace that connects freelancers with
      businesses. The Platform includes:
    </ModalText>
    <ModalText>- Freelancer profiles and discovery.</ModalText>
    <ModalText>
      - Subscriptions for freelancers (different service tiers).
    </ModalText>
    <ModalText>- Business project postings and hiring tools.</ModalText>
    <ModalText>- Secure payments processed through our payment partner.</ModalText>
    <ModalText>
      - Dispute resolution services for project-related issues.
    </ModalText>
    <ModalText>- Sponsored content and advertising placements</ModalText>

    <ModalHeading2>3. Accounts</ModalHeading2>
    <ModalText>
      - Users must provide accurate and truthful information.
    </ModalText>
    <ModalText>
      - Vetted freelancers may be required to provide government ID (e.g.,
      Emirates ID, passport) and other certifications or credentials relevant to
      their services for verification.
    </ModalText>
    <ModalText>
      - Liber reserves the right to suspend or terminate accounts for
      violations of these Terms.
    </ModalText>

    <ModalHeading2>4. Subscriptions, Fees & Payments</ModalHeading2>
    <ModalText>
      - Freelancers: Pay subscription fees to access premium features (tiers as
      published on the Platform). In addition, when a freelancer is paid by a
      client, Liber charges a platform commission on the total payment amount before
      the funds are released to the freelancer.
    </ModalText>
    <ModalText>
      - Businesses: No subscription fees; however, a transaction fee is
      charged per project.
    </ModalText>
    <ModalText>
      - Payments: Businesses pay freelancers through the Platform. Funds are
      held in Liber-X escrow until project completion and approval by the business.
    </ModalText>
    <ModalText>
      - Refunds: Our payment partner processes payment refunds. Work quality disputes are
      mediated by Liber, and resolutions may affect payment release.
    </ModalText>

    <ModalHeading2>5. User Conduct</ModalHeading2>
    <ModalText>Users must not:</ModalText>
    <ModalText>
      - Post illegal, fraudulent, misleading, or harmful content.
    </ModalText>
    <ModalText>- Harass, abuse, or discriminate against others.</ModalText>
    <ModalText>
      - Post spam, malware, or unauthorized advertising.
    </ModalText>
    <ModalText>- Circumvent Liber's systems to avoid fees.</ModalText>
    <ModalText>
      Liber reserves the right to suspend or terminate accounts for misconduct
      or breaches of these Terms.
    </ModalText>

    <ModalHeading2>6. Intellectual Property</ModalHeading2>
    <ModalText>- Freelancers retain ownership of their work.</ModalText>
    <ModalText>
      - Businesses receive rights to use work once full payment is made.
    </ModalText>
    <ModalText>
      - Liber does not claim ownership of user content but is granted a limited
      license to display it on the Platform (e.g., for profiles, portfolio
      showcases, or marketing).
    </ModalText>

    <ModalHeading2>7. Sponsored Content & Marketing</ModalHeading2>
    <ModalText>
      - Liber may host sponsored content and ads on the Platform.
    </ModalText>
    <ModalText>
      - By registering, users consent to receive newsletters, promotional
      emails, and marketing communications. Users may opt out at any time.
    </ModalText>

    <ModalHeading2>8. Limitation of Liability</ModalHeading2>
    <ModalText>
      - Liber acts as an intermediary and is not a party to contracts between
      freelancers and businesses.
    </ModalText>
    <ModalText>
      - Liber is not liable for user conduct, project outcomes, loss of data, or
      external links.
    </ModalText>
    <ModalText>
      - Services are provided "as is" without warranty of any kind.
    </ModalText>
    <ModalText>
      - To the maximum extent permitted by law, Liber's liability is limited to
      the amount of fees paid by the user in the 6 months preceding the claim.
    </ModalText>

    <ModalHeading2>9. Dispute Resolution</ModalHeading2>
    <ModalText>- Payment/refund disputes are processed by our payment partner.</ModalText>
    <ModalText>
      - Work quality disputes are mediated by Liber; decisions made in good
      faith are final.
    </ModalText>
    <ModalText>
      - Any legal disputes arising under these Terms shall be governed by the
      laws of the United Arab Emirates and subject to the jurisdiction of the
      Sharjah courts.
    </ModalText>

    <ModalHeading2>10. Termination</ModalHeading2>
    <ModalText>
      Liber may suspend or terminate your account at any time, with or without
      notice, if you breach these Terms or if required by law.
    </ModalText>

    <ModalHeading2>11. Updates to These Terms</ModalHeading2>
    <ModalText>
      Liber may update these Terms from time to time. Users will be notified of
      significant changes by email or via the Platform. Continued use of Liber
      after changes constitutes acceptance of the new Terms.
    </ModalText>

    <ModalHeading2>12. Contact</ModalHeading2>
    <ModalText>Liber FZE</ModalText>
    <ModalText>Sharjah Free Zone, UAE</ModalText>
    <ModalText>Email: info@liber-x.com</ModalText>
  </ModalContentContainer>
);

const PrivacyModalContent = () => (
  <ModalContentContainer>
    <ModalHeading1>Privacy Policy</ModalHeading1>
    <ModalText>Effective Date: 29-09-2025</ModalText>
    <ModalText>Last Updated: 29-09-2025</ModalText>

    <ModalHeading2>1. Who We Are</ModalHeading2>
    <ModalText>
      This Privacy Policy applies to Liber FZE ("Liber", "we", "our", "us"), a
      company registered in Sharjah Free Zone, United Arab Emirates. We provide
      a freelance marketplace that connects freelancers and businesses, with
      additional services including subscriptions, Liber Learn (third-party
      digital courses), a freelancer reward scheme, and advertising
      opportunities.
    </ModalText>
    <ModalText>Contact email: info@liber-x.com</ModalText>

    <ModalHeading2>2. Scope</ModalHeading2>
    <ModalText>This Policy covers all personal data processed through:</ModalText>
    <ModalText>- Our platform (website and mobile app).</ModalText>
    <ModalText>- Freelancer and business accounts.</ModalText>
    <ModalText>- ID verification for vetted freelancers.</ModalText>
    <ModalText>- Secure payment processing.</ModalText>
    <ModalText>- Support and dispute resolution.</ModalText>
    <ModalText>- Marketing communications (emails/newsletters).</ModalText>
    <ModalText>- Ads and sponsorship content hosted on Liber.</ModalText>

    <ModalHeading2>3. Information We Collect</ModalHeading2>
    <ModalText>
      <strong>A. Information You Provide</strong>
    </ModalText>
    <ModalText>
      - Account Information: name, email, phone, password, photo, biography,
      work history, skills, and portfolios.
    </ModalText>
    <ModalText>
      - Business Information: company details, projects, hiring preferences.
    </ModalText>
    <ModalText>
      - Communications: proposals, messages, attachments, reviews, feedback,
      and support tickets.
    </ModalText>
    <ModalText>
      - Verification (vetted freelancers): government ID (e.g., Emirates ID,
      passport) and related documents.
    </ModalText>
    <ModalText>
      - Liber Learn purchases: if you buy digital courses, you will be
      redirected to the seller; Liber does not process course content directly.
    </ModalText>
    <ModalText>
      <strong>B. Information Collected Automatically</strong>
    </ModalText>
    <ModalText>- IP address, browser/OS, device identifiers.</ModalText>
    <ModalText>
      - Usage logs, clicks, referrals, and session duration.
    </ModalText>
    <ModalText>
      - Cookies and analytics tools (general—specific vendors may be added
      later).
    </ModalText>
    <ModalText>
      <strong>C. Payment Data</strong>
    </ModalText>
    <ModalText>
      - All payments and card/bank details are processed exclusively by our payment partner
      UAE.
    </ModalText>
    <ModalText>
      - Liber does not store or access your full payment credentials.
    </ModalText>
    <ModalText>
      - For disputes related to work quality, Liber will mediate. For
      payment/refund processing, our payment partner handles directly.
    </ModalText>

    <ModalHeading2>4. How We Use Your Data</ModalHeading2>
    <ModalText>We process your data to:</ModalText>
    <ModalText>1. Operate the platform, accounts, and profiles.</ModalText>
    <ModalText>2. Verify freelancers through ID checks.</ModalText>
    <ModalText>3. Match freelancers and businesses.</ModalText>
    <ModalText>4. Process payments securely through our payment partner.</ModalText>
    <ModalText>5. Handle disputes and support requests.</ModalText>
    <ModalText>
      6. Send service updates, newsletters, and marketing (you may opt out).
    </ModalText>
    <ModalText>7. Enable ads and sponsored content.</ModalText>
    <ModalText>
      8. Improve services through analytics and feedback.
    </ModalText>
    <ModalText>
      9. Comply with UAE law and other applicable regulations.
    </ModalText>

    <ModalHeading2>5. Legal Basis for Processing</ModalHeading2>
    <ModalText>
      - Contractual necessity: providing the platform, payments, and projects.
    </ModalText>
    <ModalText>
      - Consent: newsletters, marketing emails, optional cookies.
    </ModalText>
    <ModalText>
      - Legal obligation: UAE tax/commercial record-keeping, ID verification if
      required by regulators.
    </ModalText>
    <ModalText>
      - Legitimate interests: fraud prevention, community safety, improving
      platform features.
    </ModalText>

    <ModalHeading2>6. Data Sharing & Disclosure</ModalHeading2>
    <ModalText>We may share data with:</ModalText>
    <ModalText>
      - Other users (limited to profiles, proposals, and messages).
    </ModalText>
    <ModalText>- Our payment partner (payments, refunds, disputes).</ModalText>
    <ModalText>- Third-party course sellers (Liber Learn).</ModalText>
    <ModalText>
      - Service providers (hosting, analytics, support, ads).
    </ModalText>
    <ModalText>
      - Regulators, auditors, or legal authorities if required.
    </ModalText>
    <ModalText>
      - Buyers or successors in a merger/acquisition.
    </ModalText>
    <ModalText>
      We do not sell personal data. If California residents' data is ever
      shared for cross-context advertising, a "Do Not Sell or Share My Personal
      Information" link will be activated.
    </ModalText>

    <ModalHeading2>7. Data Retention</ModalHeading2>
    <ModalText>
      We apply standard retention practices:
    </ModalText>
    <ModalText>
      - Inactive accounts: deleted after a standard period (12–24 months).
    </ModalText>
    <ModalText>- Support tickets: retained 12–24 months.</ModalText>
    <ModalText>
      - Financial records: retained for 5 years (per UAE requirements).
    </ModalText>
    <ModalText>
      - Verification documents: kept only as long as needed for vetting.
    </ModalText>
    <ModalText>
      - Analytics data: anonymized or aggregated where possible.
    </ModalText>

    <ModalHeading2>8. Security</ModalHeading2>
    <ModalText>- Encrypted transmission and storage of data.</ModalText>
    <ModalText>
      - Our payment partner handles all payment data under PCI DSS standards.
    </ModalText>
    <ModalText>- Access restricted to authorised staff.</ModalText>
    <ModalText>
      - Fraud detection, incident monitoring, and response protocols.
    </ModalText>
    <ModalText>- Regular reviews of vendors and platform security.</ModalText>

    <ModalHeading2>9. Your Rights</ModalHeading2>
    <ModalText>
      Depending on jurisdiction (UAE PDPL, GDPR, CCPA), you may request:
    </ModalText>
    <ModalText>- Access to your data.</ModalText>
    <ModalText>- Correction of inaccurate data.</ModalText>
    <ModalText>
      - Deletion of your account/data ("right to be forgotten").
    </ModalText>
    <ModalText>- Restriction of processing.</ModalText>
    <ModalText>
      - Data portability (copy in structured format).
    </ModalText>
    <ModalText>- Objection to marketing or profiling.</ModalText>
    <ModalText>
      Request process: send an email to info@liber-x.com. We will verify your
      identity via your account email before acting.
    </ModalText>

    <ModalHeading2>10. Children's Privacy</ModalHeading2>
    <ModalText>
      Liber is intended for 18+ only. We do not knowingly collect data from
      minors.
    </ModalText>

    <ModalHeading2>11. Automated Decision-Making</ModalHeading2>
    <ModalText>
      We use algorithms for matching freelancers with businesses and reward
      scheme calculations. These do not produce legal effects. You may contact
      us to request human review.
    </ModalText>

    <ModalHeading2>12. Cookies & Analytics</ModalHeading2>
    <ModalText>We use cookies for:</ModalText>
    <ModalText>
      - Essential: login, security, and fraud prevention.
    </ModalText>
    <ModalText>
      - Analytics: to understand use of our services.
    </ModalText>
    <ModalText>
      - Advertising: sponsored content and potential third-party ad tracking.
    </ModalText>
    <ModalText>
      Users may manage cookies through browser settings or opt-out links when
      available.
    </ModalText>

    <ModalHeading2>13. Data Transfers</ModalHeading2>
    <ModalText>
      Data may be transferred outside the UAE to service providers (e.g.,
      hosting, analytics). Safeguards include contracts and security measures
      consistent with UAE law and international standards.
    </ModalText>

    <ModalHeading2>14. Breach Response</ModalHeading2>
    <ModalText>
      If a personal data breach occurs, we will notify the relevant UAE
      authority and affected users without undue delay and, where applicable,
      follow GDPR's 72-hour notification standard.
    </ModalText>

    <ModalHeading2>15. Updates</ModalHeading2>
    <ModalText>
      We may amend this Privacy Policy from time to time. Significant changes
      will be communicated via email or platform notices.
    </ModalText>

    <ModalHeading2>16. Contact</ModalHeading2>
    <ModalText>Liber FZE</ModalText>
    <ModalText>Sharjah Free Zone, UAE</ModalText>
    <ModalText>Email: info@liber-x.com</ModalText>
  </ModalContentContainer>
);
